import React, { useState, useEffect } from 'react';
import {
  UserCircle, Buildings, Lock, PencilSimple, Check, X, Eye, EyeSlash
} from '@phosphor-icons/react/ssr';
import { api } from './services/apiClient';

const FIELD = ({ label, value, onChange, type = 'text', readOnly = false, placeholder = '' }) => {
  // ID derivado del label para que getByLabel funcione en tests
  const inputId = `profile-field-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
  return (
  <div>
    <label htmlFor={inputId} className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
      {label}
    </label>
    <input
      id={inputId}
      type={type}
      value={value ?? ''}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      className="w-full rounded-lg px-3 py-2 text-sm border outline-none transition-colors"
      style={{
        background:   readOnly ? 'var(--surface-raised)' : 'var(--surface)',
        color:        readOnly ? 'var(--text-secondary)' : 'var(--text)',
        borderColor:  'var(--border)',
        cursor:       readOnly ? 'default' : 'text',
      }}
      onFocus={e => { if (!readOnly) e.target.style.borderColor = 'var(--primary)'; }}
      onBlur={e  => { e.target.style.borderColor = 'var(--border)'; }}
    />
  </div>
  );
};

const ProfileModule = ({ useAuth, useNotification, BaseButton, BaseCard }) => {
  const { user: currentUser, updateUser } = useAuth();
  const { addNotification } = useNotification();

  const [tab, setTab]             = useState('profile');
  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  // Datos del perfil personal (editable)
  const [profileForm, setProfileForm] = useState({ fullName: '', email: '' });

  // Datos de empresa (editable)
  const [companyForm, setCompanyForm] = useState({
    contactName: '', contactEmail: '', phone: '',
    taxId: '', address: '', city: '', country: ''
  });

  // Formulario de cambio de contraseña (inline)
  const [showPwdForm, setShowPwdForm]       = useState(false);
  const [showPwd, setShowPwd]               = useState({ current: false, new: false, confirm: false });
  const [pwdForm, setPwdForm]               = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdError, setPwdError]             = useState('');
  const [savingPwd, setSavingPwd]           = useState(false);

  const isAdminOrHigher = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/api/profile')
      .then(data => {
        setProfile(data);
        setProfileForm({ fullName: data.fullName ?? '', email: data.email ?? '' });
        if (data.tenant) {
          setCompanyForm({
            contactName:  data.tenant.contactName  ?? '',
            contactEmail: data.tenant.contactEmail ?? '',
            phone:        data.tenant.phone        ?? '',
            taxId:        data.tenant.taxId        ?? '',
            address:      data.tenant.address      ?? '',
            city:         data.tenant.city         ?? '',
            country:      data.tenant.country      ?? '',
          });
        }
      })
      .catch(() => addNotification('Error al cargar el perfil', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // ── Guardar perfil personal ────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const data = await api.put('/api/profile', profileForm);
      setProfile(prev => ({ ...prev, ...data }));
      // Actualizar el nombre en el AuthContext para que el sidebar se refresque
      updateUser({ fullName: data.fullName, email: data.email });
      addNotification('Perfil actualizado correctamente', 'success');
    } catch (err) {
      addNotification(err.message || 'Error al guardar el perfil', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Guardar datos de empresa ───────────────────────────────────────────────
  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      const data = await api.put('/api/profile/company', companyForm);
      setProfile(prev => ({ ...prev, tenant: { ...prev.tenant, ...data } }));
      // Si el perfil quedó completo, quitar el banner
      if (data.isProfileComplete) {
        updateUser({ isProfileComplete: true });
        addNotification('Perfil de empresa completado', 'success');
      } else {
        addNotification('Datos de empresa guardados', 'success');
      }
    } catch (err) {
      addNotification(err.message || 'Error al guardar los datos de empresa', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Cambiar contraseña ─────────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (pwdForm.newPwd.length < 8) { setPwdError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (pwdForm.newPwd !== pwdForm.confirm) { setPwdError('Las contraseñas no coinciden.'); return; }
    setSavingPwd(true);
    try {
      await api.patch('/api/profile/password', {
        currentPassword: pwdForm.current || undefined,
        newPassword: pwdForm.newPwd
      });
      addNotification('Contraseña cambiada correctamente', 'success');
      setShowPwdForm(false);
      setPwdForm({ current: '', newPwd: '', confirm: '' });
    } catch (err) {
      setPwdError(err.message || 'Error al cambiar la contraseña');
    } finally {
      setSavingPwd(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Cargando perfil…</p>
      </div>
    );
  }

  const missingFields = profile?.tenant && !profile.tenant.isProfileComplete
    ? [
        !companyForm.contactName  && 'Nombre de contacto',
        !companyForm.contactEmail && 'Email de contacto',
        !companyForm.phone        && 'Teléfono',
      ].filter(Boolean)
    : [];

  return (
    <div className="max-w-2xl space-y-6">
      {/* ── Título ── */}
      <div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Mi perfil</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Gestiona tu información personal y los datos de tu empresa
        </p>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
        {[
          { id: 'profile', label: 'Mi perfil',  icon: UserCircle },
          ...(isAdminOrHigher ? [{ id: 'company', label: 'Mi empresa', icon: Buildings }] : [])
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            style={{
              borderColor: tab === id ? 'var(--primary)' : 'transparent',
              color:       tab === id ? 'var(--primary)' : 'var(--text-secondary)',
            }}
          >
            <Icon size={16} />
            {label}
            {id === 'company' && missingFields.length > 0 && (
              <span className="ml-1 w-2 h-2 rounded-full bg-orange-400 inline-block" title="Hay campos incompletos" />
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Mi perfil ── */}
      {tab === 'profile' && (
        <div className="space-y-5">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            {profile?.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt="Avatar"
                className="w-16 h-16 rounded-full object-cover"
                style={{ border: '2px solid var(--border)' }}
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
                style={{ background: 'var(--primary)' }}
              >
                {(profile?.fullName || profile?.username || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-semibold" style={{ color: 'var(--text)' }}>
                {profile?.fullName || profile?.username}
              </p>
              <p className="text-sm capitalize" style={{ color: 'var(--text-secondary)' }}>
                {profile?.role} · {profile?.tenantName}
              </p>
            </div>
          </div>

          {/* Campos editables */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FIELD
              label="Nombre completo"
              value={profileForm.fullName}
              onChange={e => setProfileForm(f => ({ ...f, fullName: e.target.value }))}
              placeholder="Tu nombre completo"
            />
            <FIELD
              label="Email"
              type="email"
              value={profileForm.email}
              onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
            />
            <FIELD label="Usuario" value={profile?.username} readOnly />
            <FIELD label="Rol" value={{ superadmin: 'Superadministrador', admin: 'Administrador', user: 'Usuario' }[profile?.role] ?? profile?.role} readOnly />
          </div>

          <div className="flex justify-end">
            <BaseButton onClick={handleSaveProfile} disabled={saving} size="sm">
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </BaseButton>
          </div>

          {/* Separador */}
          <div style={{ borderTop: '1px solid var(--border)' }} />

          {/* Cambio de contraseña */}
          {!showPwdForm ? (
            <button
              onClick={() => setShowPwdForm(true)}
              className="flex items-center gap-2 text-sm font-medium transition-colors"
              style={{ color: 'var(--primary)' }}
            >
              <Lock size={15} />
              Cambiar contraseña
            </button>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-3">
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Cambiar contraseña</p>

              {/* Contraseña actual — solo si no es mustChangePassword */}
              {!currentUser?.mustChangePassword && (
                <div className="relative">
                  <FIELD
                    label="Contraseña actual"
                    type={showPwd.current ? 'text' : 'password'}
                    value={pwdForm.current}
                    onChange={e => setPwdForm(f => ({ ...f, current: e.target.value }))}
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, current: !p.current }))}
                    className="absolute right-3 top-7" style={{ color: 'var(--text-secondary)' }}>
                    {showPwd.current ? <EyeSlash size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              )}

              <div className="relative">
                <FIELD
                  label="Nueva contraseña"
                  type={showPwd.new ? 'text' : 'password'}
                  value={pwdForm.newPwd}
                  onChange={e => setPwdForm(f => ({ ...f, newPwd: e.target.value }))}
                  placeholder="Mínimo 8 caracteres"
                />
                <button type="button" onClick={() => setShowPwd(p => ({ ...p, new: !p.new }))}
                  className="absolute right-3 top-7" style={{ color: 'var(--text-secondary)' }}>
                  {showPwd.new ? <EyeSlash size={15} /> : <Eye size={15} />}
                </button>
              </div>

              <div className="relative">
                <FIELD
                  label="Confirmar nueva contraseña"
                  type={showPwd.confirm ? 'text' : 'password'}
                  value={pwdForm.confirm}
                  onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))}
                />
                <button type="button" onClick={() => setShowPwd(p => ({ ...p, confirm: !p.confirm }))}
                  className="absolute right-3 top-7" style={{ color: 'var(--text-secondary)' }}>
                  {showPwd.confirm ? <EyeSlash size={15} /> : <Eye size={15} />}
                </button>
              </div>

              {pwdError && <p className="text-xs text-red-500">{pwdError}</p>}

              <div className="flex gap-2">
                <BaseButton type="submit" size="sm" disabled={savingPwd}>
                  {savingPwd ? 'Guardando…' : 'Cambiar contraseña'}
                </BaseButton>
                <BaseButton variant="outline" size="sm" type="button"
                  onClick={() => { setShowPwdForm(false); setPwdForm({ current: '', newPwd: '', confirm: '' }); setPwdError(''); }}>
                  Cancelar
                </BaseButton>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── Tab: Mi empresa ── */}
      {tab === 'company' && isAdminOrHigher && (
        <div className="space-y-5">
          {/* Aviso campos faltantes */}
          {missingFields.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg px-4 py-3 text-sm"
              style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
              <span className="font-medium">Campos requeridos pendientes:</span>
              {missingFields.join(', ')}
            </div>
          )}

          {/* Nombre (readonly — se define al registrarse) */}
          <FIELD label="Nombre de la empresa" value={profile?.tenant?.name} readOnly />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FIELD
              label="Nombre de contacto"
              value={companyForm.contactName}
              onChange={e => setCompanyForm(f => ({ ...f, contactName: e.target.value }))}
              placeholder="Persona de contacto principal"
            />
            <FIELD
              label="Email de contacto"
              type="email"
              value={companyForm.contactEmail}
              onChange={e => setCompanyForm(f => ({ ...f, contactEmail: e.target.value }))}
            />
            <FIELD
              label="Teléfono"
              value={companyForm.phone}
              onChange={e => setCompanyForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+57 300 000 0000"
            />
            <FIELD
              label="NIT / RUT"
              value={companyForm.taxId}
              onChange={e => setCompanyForm(f => ({ ...f, taxId: e.target.value }))}
              placeholder="Opcional"
            />
            <FIELD
              label="Dirección"
              value={companyForm.address}
              onChange={e => setCompanyForm(f => ({ ...f, address: e.target.value }))}
              placeholder="Opcional"
            />
            <FIELD
              label="Ciudad"
              value={companyForm.city}
              onChange={e => setCompanyForm(f => ({ ...f, city: e.target.value }))}
            />
            <FIELD
              label="País"
              value={companyForm.country}
              onChange={e => setCompanyForm(f => ({ ...f, country: e.target.value }))}
            />
          </div>

          {/* Estado perfil */}
          <div className="flex items-center gap-2 text-xs"
            style={{ color: profile?.tenant?.isProfileComplete ? '#22c55e' : '#f59e0b' }}>
            {profile?.tenant?.isProfileComplete
              ? <><Check size={14} /> Perfil de empresa completo</>
              : <><X size={14} weight="bold" /> Completa los campos requeridos para un mejor servicio</>
            }
          </div>

          <div className="flex justify-end">
            <BaseButton onClick={handleSaveCompany} disabled={saving} size="sm">
              {saving ? 'Guardando…' : 'Guardar datos de empresa'}
            </BaseButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileModule;
